import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDateTime, todayISO } from "@/lib/utils-app";
import { toast } from "sonner";
import { Search, ImageOff } from "lucide-react";

export default function ReportsPage() {
  const [parks, setParks] = useState([]);
  const [users, setUsers] = useState([]);
  const [att, setAtt] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    park_id: "all", supervisor_id: "all", date_from: todayISO(), date_to: todayISO(),
    status: "all", face_status: "all", captured_by_role: "all",
  });

  useEffect(() => {
    Promise.all([api.get("/parks"), api.get("/users")]).then(([p, u]) => {
      setParks(p.data); setUsers(u.data);
      run();
    });
    // eslint-disable-next-line
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.park_id !== "all") params.park_id = filters.park_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.face_status !== "all") params.face_status = filters.face_status;
      if (filters.captured_by_role !== "all") params.captured_by_role = filters.captured_by_role;
      const a = await api.get("/attendance", { params });
      setAtt(a.data);
      const vparams = { ...params };
      delete vparams.status; delete vparams.face_status; delete vparams.captured_by_role;
      if (filters.supervisor_id !== "all") vparams.supervisor_id = filters.supervisor_id;
      const v = await api.get("/park-visits", { params: vparams });
      setVisits(v.data);
    } catch (e) { toast.error("Failed to load reports"); }
    finally { setLoading(false); }
  };

  const csvDownloadAtt = () => {
    const rows = [["date", "employee", "code", "park", "role", "status", "face_status", "face_distance", "inside_geofence", "captured_by"]];
    for (const r of att) rows.push([r.date, r.employee_name, r.employee_code, r.park_name, r.captured_by_role, r.status, r.face_status, r.face_distance ?? "", r.inside_geofence ?? "", r.captured_by_name]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "attendance.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <AppShell title="Reports" subtitle="Filter attendance and park visits">
      <Card className="mb-4">
        <CardContent className="grid grid-cols-2 gap-3 p-4 md:grid-cols-7">
          <FF label="From"><Input type="date" value={filters.date_from} onChange={(e)=>setFilters({...filters, date_from: e.target.value})} data-testid="reports-date-from" /></FF>
          <FF label="To"><Input type="date" value={filters.date_to} onChange={(e)=>setFilters({...filters, date_to: e.target.value})} data-testid="reports-date-to" /></FF>
          <FF label="Park"><Select value={filters.park_id} onValueChange={(v)=>setFilters({...filters, park_id: v})}><SelectTrigger data-testid="reports-park-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All parks</SelectItem>{parks.map((p)=><SelectItem key={p.id} value={p.id}><span className="font-mono mr-1.5 text-muted-foreground">{p.code}</span>{p.name}</SelectItem>)}</SelectContent></Select></FF>
          <FF label="Supervisor"><Select value={filters.supervisor_id} onValueChange={(v)=>setFilters({...filters, supervisor_id: v})}><SelectTrigger data-testid="reports-supervisor-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{users.filter((u)=>u.role==="pmc_supervisor").map((u)=><SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent></Select></FF>
          <FF label="Status"><Select value={filters.status} onValueChange={(v)=>setFilters({...filters, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="marked">marked</SelectItem><SelectItem value="validated">validated</SelectItem><SelectItem value="rejected">rejected</SelectItem></SelectContent></Select></FF>
          <FF label="Face"><Select value={filters.face_status} onValueChange={(v)=>setFilters({...filters, face_status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="match">match</SelectItem><SelectItem value="mismatch">mismatch</SelectItem><SelectItem value="no_face">no_face</SelectItem><SelectItem value="multi_face">multi_face</SelectItem></SelectContent></Select></FF>
          <FF label=" "><Button onClick={run} disabled={loading} data-testid="reports-apply-filters-button"><Search className="mr-2 h-4 w-4" />Apply</Button></FF>
        </CardContent>
      </Card>

      <Tabs defaultValue="attendance">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="reports-tab-attendance">Attendance ({att.filter((r) => (r.employee_name || r.employee_code || r.photo_b64)).length})</TabsTrigger>
          <TabsTrigger value="visits" data-testid="reports-tab-visits">Park visits ({visits.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="attendance">
          <div className="flex justify-end my-2"><Button variant="outline" size="sm" onClick={csvDownloadAtt} data-testid="reports-attendance-csv">Download CSV</Button></div>
          {(() => {
            // Filter out orphan rows: attendance whose employee has been
            // deleted (no name AND no code AND no photo). These rows can't
            // be acted on and only confuse the report.
            const visible = att.filter((r) => (r.employee_name || r.employee_code || r.photo_b64));
            if (visible.length === 0) {
              return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No records.</CardContent></Card>;
            }
            return (
              <div className="flex flex-col gap-2" data-testid="reports-attendance-list">
                {visible.map((r) => (
                  <Card key={r.id}><CardContent className="flex items-center gap-3 p-3">
                    <img src={r.photo_b64?.startsWith("data:") ? r.photo_b64 : `data:image/jpeg;base64,${r.photo_b64}`} className="h-12 w-12 rounded object-cover border" alt="" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{r.employee_name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{r.employee_code}</span>
                        <StatusBadge kind={r.status} />
                        {r.face_status ? <StatusBadge kind={r.face_status} /> : null}
                        {typeof r.face_distance === "number" && <span className="font-mono text-[11px]">{r.face_distance.toFixed(3)}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{r.park_name} · {r.captured_by_role.replace("_", " ")} · {fmtDateTime(r.created_at)}</div>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            );
          })()}
        </TabsContent>
        <TabsContent value="visits">
          {visits.length === 0 ? <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No visits.</CardContent></Card>
            : <div className="flex flex-col gap-2" data-testid="reports-visits-list">
              {visits.map((v) => {
                // Fall back through available photos; if none, render a placeholder.
                const photo = v.work_photo_b64 || v.selfie_b64 || "";
                const photoSrc = photo
                  ? (photo.startsWith("data:") ? photo : `data:image/jpeg;base64,${photo}`)
                  : "";
                return (
                <Card key={v.id}><CardContent className="flex items-center gap-3 p-3">
                  {photoSrc ? (
                    <img src={photoSrc} className="h-12 w-12 rounded object-cover border" alt="" />
                  ) : (
                    <div
                      className="grid h-12 w-12 place-items-center rounded border bg-slate-100 text-slate-400"
                      title="No photo captured for this visit"
                    >
                      <ImageOff className="h-5 w-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{v.park_name}</span>
                      <span className="rounded bg-secondary px-2 py-0.5 text-[10px] uppercase">{v.work_quality}</span>
                      {v.inside_geofence === false && <StatusBadge kind="outside" />}
                      {!photoSrc && (
                        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">no photo</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">by {v.supervisor_name} · {fmtDateTime(v.visited_at)}</div>
                    {v.notes && <div className="text-xs mt-0.5 line-clamp-2">{v.notes}</div>}
                  </div>
                </CardContent></Card>
              );})}
            </div>}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function FF({ label, children }) { return <div className="flex flex-col gap-1"><Label className="text-xs">{label}</Label>{children}</div>; }
